import { Edit, Delete, MoreHoriz } from '@workwave-tidal/core/icons'
import { ContentCopy, FileUpload, RemoveCircleOutlined, SettingsSuggest } from '@mui/icons-material'

import { preview } from '@/components/form/preview'
import { useResidentModalsApi } from '@/components/modals/ResidentModals'
import { useEditOrderButtonData } from '../ConfigurationActions/hooks/useEditOrderButtonData'
import { useUnassignButtonData } from '../ConfigurationActions/hooks/useUnassignButtonData'
import { useFitInOrderButtonData } from '../ConfigurationActions/hooks/useFitInOrderButtonData'
import { useExportOrderButtonData } from '../ConfigurationActions/hooks/useExportOrderButtonData'
import { useDuplicateButtonData } from '../ConfigurationActions/hooks/useDuplicateButtonData'
import { useTexts } from '../ConfigurationActions/useTexts'
import { useExecutionOrderActions } from './hooks/useExecutionOrderActions'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
}
export function ExecutionActions(props: Props) {
  const { extOrder } = props
  const texts = useTexts()

  const {
    deleteOrders: { show: handleDeleteOrders },
    duplicateOrders: { show: handleDuplicateOrders },
  } = useResidentModalsApi()

  // Edit Button Data - Set targetTab to 1 for execution edit form
  const { editOrder } = useEditOrderButtonData(extOrder, 1)

  // Unassign Button Data
  const { unassignOrder, unassignOrderTooltip, unassignOrderDisabled, UnassignOrderModal } =
    useUnassignButtonData(extOrder)

  // Fit In Order Button Data
  const { fitInOrder, FitInOrderModal } = useFitInOrderButtonData()

  // Export Button Data
  const { exportOrder } = useExportOrderButtonData(extOrder, 'execution')

  // Duplicate Button Data
  const { duplicateTooltip, duplicateDisabled } = useDuplicateButtonData()

  const { isUnassigned } = extOrder

  const { canEditExecutionEvents, canExportExecutionOrder } = useExecutionOrderActions(extOrder)

  return (
    <>
      <preview.ActionBar
        testId="order-execution-actions"
        limit={3}
        menuIcon={<MoreHoriz />}
        menuMode="textOnly"
        menuTooltip={texts.more}
      >
        <preview.ActionBarItem
          key="edit"
          icon={<Edit />}
          label={texts.editOrder}
          tooltip={texts.editOrder}
          onClick={editOrder}
          data-testid="orders-execution__edit-button"
          data-trackid="orders-execution__edit-button"
          disabled={!canEditExecutionEvents}
          disableWhenReadOnly
        />

        {!isUnassigned ? (
          <preview.ActionBarItem
            key="unassign"
            icon={<RemoveCircleOutlined />}
            label={texts.unassignOrder}
            tooltip={unassignOrderTooltip}
            onClick={unassignOrder}
            disabled={unassignOrderDisabled}
            data-testid="orders-execution__unassign-button"
            data-trackid="orders-execution__unassign-button"
            disableWhenReadOnly
          />
        ) : (
          <preview.ActionBarItem
            key="fit-in"
            icon={<SettingsSuggest />}
            label={texts.fitInOrder}
            tooltip={texts.fitInOrder}
            onClick={fitInOrder}
            data-testid="orders-execution__fit-in-button"
            data-trackid="orders-execution__fit-in-button"
            disableWhenReadOnly
          />
        )}

        <preview.ActionBarItem
          key="delete"
          icon={<Delete />}
          label={texts.deleteOrder}
          tooltip={texts.deleteOrder}
          onClick={() => handleDeleteOrders([extOrder.id])}
          data-testid="orders-execution__delete-button"
          data-trackid="orders-execution__delete-button"
          disableWhenReadOnly
        />

        <preview.ActionBarItem
          key="duplicate"
          icon={<ContentCopy />}
          label={texts.duplicate}
          onClick={() => handleDuplicateOrders([extOrder.id])}
          tooltip={duplicateTooltip}
          disabled={duplicateDisabled}
          data-testid="orders-execution__duplicate-button"
          data-trackid="orders-execution__duplicate-button"
          disableWhenReadOnly
        />

        {canExportExecutionOrder ? (
          <preview.ActionBarItem
            key="export"
            icon={<FileUpload />}
            label={texts.exportExecution}
            onClick={exportOrder}
            tooltip={texts.exportExecution}
            data-testid="orders-execution__export-button"
            data-trackid="orders-execution__export-button"
            disableWhenReadOnly={false}
          />
        ) : (
          <></>
        )}
      </preview.ActionBar>

      <UnassignOrderModal orderStepIds={[extOrder.id]} />
      <FitInOrderModal orderStepIds={[extOrder.id]} />
    </>
  )
}
